import { useTranslate } from '@refinedev/core';
import { List } from '@refinedev/antd';
import { Table, Input, Form, Row, Col, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { ThemaCodeOut } from '@/api/generated/schemas';
import type { TranslatedStr } from '@/api/generated/schemas';
import { useTableSearch } from '@/hooks/useTableSearch';
import { useAdminTable } from '@/hooks/useAdminTable';
import type { CrudFilter } from '@refinedev/core';
import { TextFilter } from '@/components/common/TextFilter';
import { isFieldFiltered } from '@/utils/filters';
import { getSortOrder } from '@/utils/sorters';

export const ContentCatalogThemaCodeList = () => {
  const t = useTranslate();
  const { onSearch, createDebouncedSubmit } = useTableSearch();

  const { tableProps, searchFormProps, sorters, setFilters, filters } =
    useAdminTable<ThemaCodeOut>({
      pagination: { pageSize: 20 },
      resource: 'content_catalog.thema_codes',
      onSearch,
      sorters: {
        initial: [{ field: 'id', order: 'desc' }],
      },
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);

  return (
    <List canCreate={false}>
      <Form {...searchFormProps} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }} align="middle">
          <Col xs={24} sm={16} md={12} lg={8}>
            <Form.Item name="q" style={{ marginBottom: 0 }}>
              <Input
                placeholder={t(
                  'content_catalog.thema_codes.search.placeholder'
                )}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => debouncedSubmit(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Table
        {...tableProps}
        rowKey="id"
        pagination={{
          ...(typeof tableProps.pagination === 'object'
            ? tableProps.pagination
            : {}),
          showTotal: (total) => t('base.total', { total }),
        }}
      >
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
        />

        <Table.Column
          dataIndex="code"
          title={t('content_catalog.thema_codes.fields.code')}
          sorter
          sortOrder={getSortOrder(sorters, 'code')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'code')}
          filterDropdown={({ confirm, clearFilters }) => (
            <TextFilter
              field="code"
              width={160}
              placeholder={t('content_catalog.thema_codes.fields.code')}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="description"
          title={t('content_catalog.thema_codes.fields.description')}
          render={(value: TranslatedStr | null) =>
            value?.en || value?.pl ? (
              <Space direction="vertical" size={0}>
                {value.en && <span>🇬🇧 {value.en}</span>}
                {value.pl && <span>🇵🇱 {value.pl}</span>}
              </Space>
            ) : (
              '-'
            )
          }
        />
      </Table>
    </List>
  );
};
